// hooks/usePreloadAssets.js
import { useEffect, useMemo, useRef, useState } from "react";

import { guessFamilyFromUrl } from "../helper/preloadHelper";

/**
 * usePreloadAssets(assets: (string | { id?: string, src: string })[])
 * Returns:
 *  {
 *    progress: number (0-100),
 *    loaded: boolean,
 *    errors: Array<{ id, src, error }>,
 *    results: Map(id -> { value, type, isObjectURL?: boolean })
 *  }
 */
export default function usePreloadAssets(assets = []) {
  const [progress, setProgress] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [errors, setErrors] = useState([]);
  const resultsRef = useRef(new Map());
  const mounted = useRef(true);

  // Normalize once, keep stable shape
  const fileList = useMemo(() => {
    return assets.map((a, i) =>
      typeof a === "string"
        ? { id: String(i), src: a }
        : { id: a.id ?? String(i), src: a.src }
    );
  }, [assets]);

  // Stable dependency key so we don’t reload on object identity churn
  const depKey = useMemo(
    () => fileList.map((f) => `${f.id}|${f.src}`).join("||"),
    [fileList]
  );

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    let loadedCount = 0;

    resultsRef.current = new Map();
    setErrors([]);
    setProgress(0);
    setLoaded(false);

    const total = fileList.length;

    const updateProgress = () => {
      if (!mounted.current) return;
      const p = total === 0 ? 100 : Math.round((loadedCount / total) * 100);
      setProgress(p);
      if (p === 100) setLoaded(true);
    };

    // ---------- Loaders ----------
    const loadImage = (item) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ item, value: img, type: "image" });
        img.onerror = (e) => reject({ item, error: e });
        img.src = item.src;
      });

    const loadAudio = (item) =>
      new Promise((resolve, reject) => {
        const audio = new Audio();
        audio.preload = "auto";
        const done = () => resolve({ item, value: audio, type: "audio" });
        audio.addEventListener("canplaythrough", done, { once: true });
        audio.onerror = (e) => reject({ item, error: e });
        audio.src = item.src;
        audio.load();
      });

    const loadFont = async (item) => {
      // Guard for SSR/older browsers
      if (
        typeof document === "undefined" ||
        typeof FontFace === "undefined" ||
        !document.fonts
      )
        return null;

      const ext = item.src.split("?")[0].split(".").pop().toLowerCase();
      if (!["woff2", "woff", "ttf", "otf"].includes(ext)) return null;

      // Derive a family name from id or filename if not provided externally.
      // If you prefer explicit names, pass ids like "font:Orbitron-700".
      const derivedFamily =
        (item.id && item.id.startsWith("font:") ? item.id.slice(5) : "") ||
        guessFamilyFromUrl(item.src) ||
        "AppFont";

      try {
        const face = new FontFace(derivedFamily, `url(${item.src})`, {});
        const loadedFace = await face.load();
        document.fonts.add(loadedFace);
        return { item, value: loadedFace, type: "font", family: derivedFamily };
      } catch (e) {
        throw Error;
      }
    };

    const fetchGeneric = (item) =>
      fetch(item.src)
        .then(async (res) => {
          if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
          const ct = res.headers.get("content-type") || "";
          if (ct.includes("application/json")) {
            const json = await res.json();
            return { item, value: json, type: "json" };
          }
          if (ct.startsWith("text/")) {
            const text = await res.text();
            return { item, value: text, type: "text" };
          }
          // Fallback to blob → objectURL (images, videos, etc. not covered above)
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          return { item, value: url, type: "url", isObjectURL: true, blob };
        })
        .catch((err) => Promise.reject({ item, error: err }));

    const loadOne = async (item) => {
      const ext = item.src.split("?")[0].split(".").pop().toLowerCase();

      // Fonts first (so we don’t accidentally blob them)
      const maybeFont = await loadFont(item);
      if (maybeFont) return maybeFont;

      if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)) {
        return loadImage(item);
      }
      if (["mp3", "wav", "ogg", "m4a"].includes(ext)) {
        return loadAudio(item);
      }
      // Otherwise fetch() and infer type
      return fetchGeneric(item);
    };

    const promises = fileList.map((item) =>
      loadOne(item)
        .then((res) => {
          if (cancelled) return;
          const { item: itm, value, type, isObjectURL } = res;
          resultsRef.current.set(itm.id, {
            value,
            type,
            isObjectURL: !!isObjectURL,
          });
          loadedCount++;
          updateProgress();
        })
        .catch(({ item: itm, error }) => {
          if (cancelled) return;
          setErrors((prev) => [...prev, { id: itm.id, src: itm.src, error }]);
          loadedCount++;
          updateProgress();
        })
    );

    Promise.all(promises).finally(() => {
      if (!mounted.current) return;
      setLoaded(true);
      setProgress(100);
    });

    return () => {
      cancelled = true;
      mounted.current = false;
      // Revoke any created object URLs
      for (const [, r] of resultsRef.current) {
        if (r && r.isObjectURL && typeof r.value === "string") {
          try {
            URL.revokeObjectURL(r.value);
          } catch {}
        }
      }
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey]);

  return {
    progress,
    loaded,
    errors,
    results: resultsRef.current, // Map(id -> { value, type, isObjectURL? })
  };
}
